import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Module access by tier
const MODULE_ACCESS = {
  standard: {
    essentials: true,
    calendar: true,
    analytics: true,
    training: true,
    integrations: true,
    grc: true,
    risk_register: true,
    data_protection: true,
    ai_governance: true,
    audit: true,
    quality_qms: true,
    document_library: true,
    trust_portal: true,
    // Pro modules - NOT included
    board_packs: false,
    pmo: false,
    contracts: false,
    ma: false,
    // Pro+ modules - NOT included
    deal_room: false,
    due_diligence_workflows: false,
    integration_playbooks: false
  },
  pro: {
    essentials: true,
    calendar: true,
    analytics: true,
    training: true,
    integrations: true,
    grc: true,
    risk_register: true,
    data_protection: true,
    ai_governance: true,
    audit: true,
    quality_qms: true,
    document_library: true,
    trust_portal: true,
    // Pro modules - INCLUDED
    board_packs: true,
    pmo: true,
    contracts: true,
    ma: true,
    cosec: true,
    // Pro+ modules - NOT included
    deal_room: false,
    due_diligence_workflows: false,
    integration_playbooks: false
  },
  pro_plus: {
    essentials: true,
    calendar: true,
    analytics: true,
    training: true,
    integrations: true,
    grc: true,
    risk_register: true,
    data_protection: true,
    ai_governance: true,
    audit: true,
    quality_qms: true,
    document_library: true,
    trust_portal: true,
    board_packs: true,
    pmo: true,
    contracts: true,
    ma: true,
    cosec: true,
    // Pro+ modules - INCLUDED
    ma_plus: true,
    deal_room: true,
    due_diligence_workflows: true,
    integration_playbooks: true
  },
  // Legacy mappings
  lite: null, // Maps to standard
  enterprise: null, // Maps to pro_plus
  none: null // No subscription
};

// Map legacy tier names to new ones
const TIER_MAPPING = {
  lite: 'standard',
  lite_monthly: 'standard',
  lite_annual: 'standard',
  standard: 'standard',
  standard_monthly: 'standard',
  standard_annual: 'standard',
  pro: 'pro',
  pro_monthly: 'pro',
  pro_annual: 'pro',
  professional: 'pro',
  pro_plus: 'pro_plus',
  pro_plus_monthly: 'pro_plus',
  pro_plus_annual: 'pro_plus',
  enterprise: 'pro_plus',
  enterprise_monthly: 'pro_plus',
  enterprise_annual: 'pro_plus',
  none: 'standard' // Default to standard for trial users
};

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [moduleEntitlements, setModuleEntitlements] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiCredits, setAiCredits] = useState({ used: 0, limit: 150, remaining: 150 });

  const fetchSubscription = useCallback(async () => {
    if (!user || !token) {
      setSubscription(null);
      setModuleEntitlements(null);
      setTrialStatus(null);
      setLoading(false);
      return;
    }

    try {
      // Fetch subscription info
      const response = await axios.get(`${API}/api/subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSubscription(response.data);
      
      // Fetch trial status (new modular system)
      try {
        const trialRes = await axios.get(`${API}/api/modules/trial-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setTrialStatus(trialRes.data);
      } catch (e) {
      }
      
      // ALSO fetch module entitlements (admin-granted access)
      try {
        const entitlementsRes = await axios.get(`${API}/api/modules/entitlements`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setModuleEntitlements(entitlementsRes.data);
      } catch (e) {
      }
      
      // Also fetch AI credits
      try {
        const creditsRes = await axios.get(`${API}/api/ai/credits`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setAiCredits(creditsRes.data);
      } catch (e) {
        // AI credits endpoint may not exist yet
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Get the normalized tier name
  const getNormalizedTier = () => {
    if (!subscription || !subscription.tier) return 'standard';
    const rawTier = subscription.tier.toLowerCase();
    return TIER_MAPPING[rawTier] || 'standard';
  };

  // Check if user has access to a specific module
  const hasModuleAccess = (module) => {
    // NEW: Check modular trial/subscription system first
    // Note: Check for array existence with length > 0 to avoid false negatives with empty arrays
    if (trialStatus?.active_modules && trialStatus.active_modules.length > 0) {
      // Map module names to the modular system
      const moduleMapping = {
        'grc': 'grc',
        'risk_register': 'grc',
        'data_protection': 'grc',
        'ai_governance': 'grc',
        'audit': 'grc',
        'quality_qms': 'grc',
        'document_library': 'grc',
        'trust_portal': 'grc',
        'cosec': 'cosec',
        'board_packs': 'cosec',
        'pmo': 'pmo',
        'contracts': 'contracts',
        'ma': 'ma',
        'ma_plus': 'ma_plus',
        'deal_room': 'ma_plus',
        'due_diligence_workflows': 'ma_plus',
        'integration_playbooks': 'ma_plus',
        // Core features always available
        'essentials': 'grc',
        'calendar': 'grc',
        'analytics': 'grc',
        'training': 'grc',
        'integrations': 'grc'
      };
      
      const mappedModule = moduleMapping[module] || module;
      if (trialStatus.active_modules.includes(mappedModule)) {
        return true;
      }
    }
    
    // SECOND: Check admin-granted module entitlements (highest priority)
    if (moduleEntitlements?.modules) {
      const moduleData = moduleEntitlements.modules[module];
      if (moduleData && moduleData.tier && moduleData.tier !== 'none') {
        return true; // Admin granted access
      }
    }
    
    // THIRD: Check full suite access
    if (moduleEntitlements?.has_full_suite) {
      return true;
    }
    
    // FOURTH: Fall back to tier-based access
    const tier = getNormalizedTier();
    const access = MODULE_ACCESS[tier];
    if (!access) return false;
    return access[module] === true;
  };

  // Check if a module is locked (for showing padlock icon) - single source of truth
  const isModuleLocked = (module) => !hasModuleAccess(module);

  // Get upgrade options for locked modules
  const getUpgradeOptions = () => {
    return trialStatus?.upgrade_options || null;
  };

  // Get pricing information
  const getPricing = () => {
    return trialStatus?.pricing || null;
  };

  // Check if user is on trial
  const isOnTrial = () => {
    if (trialStatus) {
      return trialStatus.trial_active === true;
    }
    return subscription?.status === 'trialing';
  };

  // Get trial days remaining
  const getTrialDaysRemaining = () => {
    return trialStatus?.trial_days_remaining || 0;
  };

  // Check if trial has expired
  const isTrialExpired = () => {
    return trialStatus?.trial_expired === true;
  };

  // Check if user is a founding member
  const isFoundingMember = () => {
    return subscription?.is_founding_member === true;
  };

  // Get the tier display name
  const getTierDisplayName = () => {
    const tier = getNormalizedTier();
    const names = {
      standard: 'Standard',
      pro: 'Pro',
      pro_plus: 'Pro+'
    };
    return names[tier] || 'Standard';
  };

  // Get required tier for a module
  const getRequiredTier = (module) => {
    // Check which tier includes this module
    if (MODULE_ACCESS.standard[module]) return 'Standard';
    if (MODULE_ACCESS.pro[module]) return 'Pro';
    if (MODULE_ACCESS.pro_plus[module]) return 'Pro+';
    return 'Pro+';
  };

  // Get upgrade message for a locked module
  const getUpgradeMessage = (module) => {
    const requiredTier = getRequiredTier(module);
    const currentTier = getTierDisplayName();
    
    if (requiredTier === currentTier) return null;
    
    const moduleNames = {
      board_packs: 'Board Packs',
      pmo: 'PMO',
      contracts: 'Contract Management',
      ma: 'M&A',
      deal_room: 'Deal Room',
      due_diligence_workflows: 'Due Diligence Workflows',
      integration_playbooks: 'Integration Playbooks'
    };
    
    return {
      module: moduleNames[module] || module,
      requiredTier,
      currentTier,
      message: `${moduleNames[module] || module} requires ${requiredTier}. Upgrade from ${currentTier} to unlock.`
    };
  };

  const value = {
    subscription,
    loading,
    tier: getNormalizedTier(),
    tierDisplayName: getTierDisplayName(),
    isOnTrial: isOnTrial(),
    trialDaysRemaining: getTrialDaysRemaining(),
    trialExpired: isTrialExpired(),
    isFoundingMember: isFoundingMember(),
    hasModuleAccess,
    isModuleLocked,
    getUpgradeOptions,
    getPricing,
    getRequiredTier,
    getUpgradeMessage,
    aiCredits,
    trialStatus,
    activeModules: trialStatus?.active_modules || [],
    lockedModules: trialStatus?.locked_modules || [],
    refreshSubscription: fetchSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
